'use client';

import { useEffect, useState } from 'react';
import { KeyRound, Loader2, Lock, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { changeEmail, changePassword } from '@/services/auth';
import { changeEmailSchema, changePasswordSchema } from '@/lib/validations';
import {
  evaluatePasswordStrength,
  type PasswordStrength,
} from '@/lib/passwordPolicy';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import type { UserDTO } from '@/types/auth';

type AccountCredentialsSectionProps = {
  email: string;
  onEmailUpdated?: (user: UserDTO) => void;
  className?: string;
};

function strengthLabel(strength: PasswordStrength): string {
  if (strength === 'weak') return 'Fraca';
  if (strength === 'medium') return 'Média';
  return 'Forte';
}

function strengthBarClass(strength: PasswordStrength): string {
  if (strength === 'weak') return 'bg-red-500 w-1/3';
  if (strength === 'medium') return 'bg-amber-500 w-2/3';
  return 'bg-teal-600 w-full';
}

export function AccountCredentialsSection({
  email,
  onEmailUpdated,
  className = '',
}: AccountCredentialsSectionProps) {
  const [displayEmail, setDisplayEmail] = useState(email);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>('weak');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // Email modal
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [draftEmail, setDraftEmail] = useState(email);
  const [emailConfirmPassword, setEmailConfirmPassword] = useState('');
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setDisplayEmail(email);
    setDraftEmail(email);
  }, [email]);

  useEffect(() => {
    setPasswordStrength(evaluatePasswordStrength(newPassword));
  }, [newPassword]);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({});
    setPasswordStrength('weak');
  };

  const openEmailModal = () => {
    setDraftEmail(displayEmail);
    setEmailConfirmPassword('');
    setEmailErrors({});
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    if (isSavingEmail) return;
    setIsEmailModalOpen(false);
    setEmailConfirmPassword('');
    setEmailErrors({});
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordErrors({});

    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] || 'form');
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setPasswordErrors(fieldErrors);
      return;
    }

    setIsSavingPassword(true);
    try {
      await changePassword({
        currentPassword: parsed.data.currentPassword,
        newPassword: parsed.data.newPassword,
      });
      toast.success('Senha alterada com sucesso');
      resetPasswordForm();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar senha';
      toast.error(message);
      setPasswordErrors({ form: message });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleChangeEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setEmailErrors({});

    const parsed = changeEmailSchema.safeParse({
      newEmail: draftEmail.trim(),
      currentPassword: emailConfirmPassword,
    });

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] || 'form');
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setEmailErrors(fieldErrors);
      return;
    }

    setIsSavingEmail(true);
    try {
      const result = await changeEmail({
        newEmail: parsed.data.newEmail,
        currentPassword: parsed.data.currentPassword,
      });
      setDisplayEmail(result.user.email);
      onEmailUpdated?.(result.user);
      toast.success(result.message || 'E-mail alterado com sucesso');
      setIsEmailModalOpen(false);
      setEmailConfirmPassword('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar e-mail';
      toast.error(message);
      setEmailErrors({ form: message });
    } finally {
      setIsSavingEmail(false);
    }
  };

  return (
    <section
      className={`bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden ${className}`}
      aria-labelledby="account-credentials-heading"
    >
      <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-xl text-teal-600">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h2
              id="account-credentials-heading"
              className="text-base font-bold text-gray-900 dark:text-white"
            >
              Dados da conta
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Altere e-mail e senha com confirmação de segurança.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Email */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <Mail className="w-4 h-4 text-teal-600" />
            E-mail
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 space-y-2">
              <Label htmlFor="account-email-display">E-mail atual</Label>
              <Input
                id="account-email-display"
                value={displayEmail}
                readOnly
                disabled
                className="bg-gray-50 dark:bg-neutral-800/80"
              />
            </div>
            <Button type="button" variant="outline" onClick={openEmailModal} className="sm:mb-0">
              Alterar e-mail
            </Button>
          </div>
        </div>

        {/* Password */}
        <form onSubmit={handleChangePassword} className="space-y-4 border-t border-gray-100 dark:border-neutral-800 pt-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <KeyRound className="w-4 h-4 text-teal-600" />
            Alterar senha
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="current-password">Senha atual</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="bg-white dark:bg-neutral-900"
              />
              {passwordErrors.currentPassword && (
                <p className="text-xs text-red-600">{passwordErrors.currentPassword}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-white dark:bg-neutral-900"
              />
              {passwordErrors.newPassword && (
                <p className="text-xs text-red-600">{passwordErrors.newPassword}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar nova senha</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white dark:bg-neutral-900"
              />
              {passwordErrors.confirmPassword && (
                <p className="text-xs text-red-600">{passwordErrors.confirmPassword}</p>
              )}
            </div>
          </div>

          {newPassword.length > 0 && (
            <div className="space-y-2 rounded-xl border border-gray-100 dark:border-neutral-800 p-3 bg-gray-50/60 dark:bg-neutral-800/30">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Força da senha</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {strengthLabel(passwordStrength)}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-neutral-700 overflow-hidden">
                <div className={`h-full transition-all ${strengthBarClass(passwordStrength)}`} />
              </div>
              {/* Troca de senha: lista só após o 1º caractere. Cadastro mostra sempre. */}
              <PasswordRequirementsChecklist password={newPassword} />
            </div>
          )}

          {passwordErrors.form && (
            <p className="text-sm text-red-600" role="alert">
              {passwordErrors.form}
            </p>
          )}

          <Button type="submit" disabled={isSavingPassword} className="bg-teal-600 hover:bg-teal-700 text-white">
            {isSavingPassword ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Salvando…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Salvar nova senha
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Modal confirmação senha para troca de e-mail */}
      <Dialog
        open={isEmailModalOpen}
        onOpenChange={(open) => {
          if (!open) closeEmailModal();
          else setIsEmailModalOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={!isSavingEmail}>
          <DialogHeader>
            <DialogTitle>Confirmar alteração de e-mail</DialogTitle>
            <DialogDescription>
              Por segurança, informe sua senha atual para confirmar a troca de e-mail.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="modal-new-email">Novo e-mail</Label>
              <Input
                id="modal-new-email"
                type="email"
                autoComplete="email"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                autoFocus
              />
              {emailErrors.newEmail && (
                <p className="text-xs text-red-600">{emailErrors.newEmail}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="modal-current-password">Senha atual</Label>
              <Input
                id="modal-current-password"
                type="password"
                autoComplete="current-password"
                value={emailConfirmPassword}
                onChange={(e) => setEmailConfirmPassword(e.target.value)}
              />
              {emailErrors.currentPassword && (
                <p className="text-xs text-red-600">{emailErrors.currentPassword}</p>
              )}
            </div>

            {emailErrors.form && (
              <p className="text-sm text-red-600" role="alert">
                {emailErrors.form}
              </p>
            )}

            <DialogFooter className="mx-0 mb-0 rounded-none border-0 bg-transparent p-0">
              <Button type="button" variant="outline" onClick={closeEmailModal} disabled={isSavingEmail}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSavingEmail}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                {isSavingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Confirmando…
                  </>
                ) : (
                  'Confirmar alteração'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
